import { useMemo, useState } from 'react'
import { motion } from 'motion/react'
import { useCompetitions, useMatches, useSettings } from '@/lib/queries'
import { cn, readMatch, toDate } from '@/lib/utils'
import { Container } from '@/components/site/primitives'
import { PageHero } from '@/components/site/page-hero'
import { MatchCard } from '@/components/site/cards'
import { Select } from '@/components/ui/field'
import { EmptyState, ErrorState, LoadingBlock } from '@/components/ui/feedback'
import { usePageMeta } from '@/components/layout/site-layout'

type Tab = 'upcoming' | 'results' | 'all'

const TABS: { key: Tab; label: string }[] = [
  { key: 'upcoming', label: 'Fixtures' },
  { key: 'results', label: 'Results' },
  { key: 'all', label: 'All' },
]

export default function MatchesPage() {
  usePageMeta('Matches', 'Fixtures, live scores and results for AFC Vikings Cairo.')

  const { data: settings } = useSettings()
  const { data: matches = [], isLoading, isError } = useMatches()
  const { data: competitions = [] } = useCompetitions()

  const [tab, setTab] = useState<Tab>('upcoming')
  const [competitionId, setCompetitionId] = useState('')

  const clubName = settings?.club_name ?? 'AFC Vikings Cairo'

  const { live, visible, counts } = useMemo(() => {
    const scoped = competitionId
      ? matches.filter((match) => match.competition === competitionId)
      : matches

    const inProgress = scoped.filter((match) => readMatch(match, clubName).isLive)
    const upcoming = scoped
      .filter((match) => match.status === 'scheduled' || match.status === 'postponed')
      .sort((a, b) => a.kickoff.localeCompare(b.kickoff))
    const results = scoped
      .filter((match) => match.status === 'finished' || match.status === 'cancelled')
      .sort((a, b) => b.kickoff.localeCompare(a.kickoff))

    const visible =
      tab === 'upcoming'
        ? upcoming
        : tab === 'results'
          ? results
          : [...inProgress, ...upcoming, ...results]

    return {
      live: inProgress,
      visible,
      counts: { upcoming: upcoming.length, results: results.length, all: scoped.length },
    }
  }, [matches, competitionId, tab, clubName])

  // Group by month so a long fixture list stays scannable.
  const months = useMemo(() => {
    const buckets = new Map<string, typeof visible>()
    for (const match of visible) {
      const date = toDate(match.kickoff)
      const key = date
        ? new Intl.DateTimeFormat('en-GB', {
            month: 'long',
            year: 'numeric',
            timeZone: 'Africa/Cairo',
          }).format(date)
        : 'Date to be confirmed'
      const bucket = buckets.get(key)
      if (bucket) bucket.push(match)
      else buckets.set(key, [match])
    }
    return [...buckets.entries()]
  }, [visible])

  return (
    <>
      <PageHero
        kicker="Fixtures & results"
        rail={live.length > 0 ? 'Live now' : 'Season'}
        title="Matches"
        subtitle="Every fixture, live score and result. Scores update the moment they are entered — no refresh needed."
      />

      <section className="bg-ink py-12 md:py-20">
        <Container>
          {isError ? (
            <ErrorState title="Matches did not load" />
          ) : isLoading ? (
            <LoadingBlock label="Loading fixtures" />
          ) : (
            <>
              {live.length > 0 && tab !== 'results' ? (
                <div className="mb-12">
                  <div className="mb-5 flex items-center gap-3">
                    <span className="animate-pulse-live bg-blood size-2 rounded-full" aria-hidden />
                    <span className="eyebrow text-blood-bright">Live right now</span>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    {live.map((match) => (
                      <MatchCard key={match.id} match={match} clubName={clubName} />
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="border-hairline flex flex-col gap-4 border-b pb-4 sm:flex-row sm:items-end sm:justify-between">
                <div className="no-scrollbar flex gap-1 overflow-x-auto" role="tablist">
                  {TABS.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      role="tab"
                      aria-selected={tab === item.key}
                      onClick={() => setTab(item.key)}
                      className={cn(
                        'condensed relative shrink-0 px-4 py-3 text-[0.8125rem] font-semibold tracking-[0.16em] transition-colors',
                        tab === item.key ? 'text-blood' : 'text-bone-faint hover:text-bone-dim'
                      )}
                    >
                      {item.label}
                      <span className="ml-2 text-[0.6875rem] opacity-60">{counts[item.key]}</span>
                      {tab === item.key ? (
                        <motion.span
                          layoutId="matches-tab"
                          className="bg-blood absolute inset-x-0 -bottom-[1.0625rem] h-0.5"
                        />
                      ) : null}
                    </button>
                  ))}
                </div>

                {competitions.length > 1 ? (
                  <Select
                    value={competitionId}
                    onChange={(event) => setCompetitionId(event.target.value)}
                    aria-label="Filter by competition"
                    className="sm:w-64"
                  >
                    <option value="">All competitions</option>
                    {competitions.map((competition) => (
                      <option key={competition.id} value={competition.id}>
                        {competition.name}
                        {competition.season ? ` · ${competition.season}` : ''}
                      </option>
                    ))}
                  </Select>
                ) : null}
              </div>

              {visible.length === 0 ? (
                <EmptyState
                  className="mt-12"
                  title={tab === 'upcoming' ? 'No fixtures scheduled' : 'No results yet'}
                  description="Matches added in the club dashboard appear here immediately."
                />
              ) : (
                <div className="mt-12 space-y-12">
                  {months.map(([month, group]) => (
                    <div key={month}>
                      <div className="mb-5 flex items-center gap-4">
                        <h2 className="condensed text-bone-dim text-[0.9375rem] font-bold tracking-[0.2em]">
                          {month.toUpperCase()}
                        </h2>
                        <span className="bg-hairline h-px flex-1" aria-hidden />
                      </div>
                      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {group.map((match) => (
                          <MatchCard key={match.id} match={match} clubName={clubName} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Container>
      </section>
    </>
  )
}
